<script setup>
// TermsPrivacyView.vue
// A simple, readable Terms of Service and Privacy Policy page.
// - Designed to work on both marketing and learn hosts
// - Uses project CSS variables for consistent theming
// - Includes anchor links for deep-linking to sections
</script>

<template>
  <div class="legal">
    <header class="header">
      <h1>Terms of Service and Privacy Policy</h1>
      <p class="intro">
        Please read these Terms of Service and Privacy Policy carefully. By using
        this service, you agree to the terms below.
      </p>
      <nav class="toc" aria-label="Table of contents">
        <a href="#terms-of-service">Terms of Service</a>
        <span aria-hidden="true">•</span>
        <a href="#privacy-policy">Privacy Policy</a>
        <span aria-hidden="true">•</span>
        <a href="#contact">Contact</a>
      </nav>
    </header>

    <main class="content">
      <section id="terms-of-service" class="section">
        <h2>Terms of Service</h2>

        <h3>1. Acceptance of Terms</h3>
        <p>
          By accessing or using this service, you agree to be bound by these
          Terms of Service. If you do not agree to all the terms, you may not
          access or use the service.
        </p>

        <h3>2. Accounts and Access</h3>
        <p>
          You are responsible for maintaining the confidentiality of your
          account credentials and for all activities that occur under your
          account. Do not share your credentials or allow others to use your
          account.
        </p>

        <h3>3. Subscriptions and Billing</h3>
        <p>
          Paid plans are billed in accordance with the plan you select. Unless
          cancelled, subscriptions renew automatically at the end of each
          billing period. You can manage or cancel your subscription at any
          time from your account settings. Applicable taxes may be added as
          required by law.
        </p>

        <h3>4. Acceptable Use</h3>
        <p>
          You agree not to misuse the service, including but not limited to:
          attempting to reverse engineer, disrupt, overload, or bypass security
          features; using the service for unlawful purposes; or infringing on
          the rights of others.
        </p>

        <h3>5. Intellectual Property</h3>
        <p>
          The service, including its content, features, and branding, is owned
          or licensed by the provider and is protected by intellectual property
          laws. You are granted a limited, non-transferable license to use the
          service for personal, non-commercial purposes in accordance with these
          terms.
        </p>

        <h3>6. Modifications to the Service</h3>
        <p>
          Features and availability may change over time. We may add, modify,
          or remove features, and may suspend or discontinue the service with or
          without notice where reasonable.
        </p>

        <h3>7. Termination</h3>
        <p>
          We may suspend or terminate access to the service if you violate these
          terms or if required by law. You may stop using the service at any
          time. Provisions that by their nature should survive termination will
          survive, including ownership, disclaimers, and limitations of
          liability.
        </p>

        <h3>8. Disclaimers</h3>
        <p>
          The service is provided “as is” and “as available” without warranties
          of any kind, express or implied, including but not limited to implied
          warranties of merchantability, fitness for a particular purpose, and
          non-infringement.
        </p>

        <h3>9. Limitation of Liability</h3>
        <p>
          To the maximum extent permitted by law, in no event will we be liable
          for indirect, incidental, special, consequential, or punitive damages,
          or any loss of profits or revenues, whether incurred directly or
          indirectly, or any loss of data, use, goodwill, or other intangible
          losses, resulting from your use of the service.
        </p>

        <h3>10. Changes to These Terms</h3>
        <p>
          We may update these terms from time to time. Material changes will be
          communicated by updating this page and/or via in-app or email notice
          where appropriate. Continued use of the service after changes become
          effective constitutes acceptance of the updated terms.
        </p>
      </section>

      <section id="privacy-policy" class="section">
        <h2>Privacy Policy</h2>

        <h3>1. Information We Collect</h3>
        <ul>
          <li>
            Account information: such as your email and profile details you
            choose to provide.
          </li>
          <li>
            Usage information: such as interactions within the app and device
            metadata to help improve the service.
          </li>
          <li>
            Payment information: handled by our payment processor. We do not
            store full payment card details on our servers.
          </li>
        </ul>

        <h3>2. How We Use Information</h3>
        <ul>
          <li>To provide, maintain, and improve the service.</li>
          <li>To personalize your learning experience.</li>
          <li>To process subscriptions and provide support.</li>
          <li>
            To communicate important updates, such as changes to features or
            policies.
          </li>
        </ul>

        <h3>3. Data Sharing</h3>
        <p>
          We do not sell your personal information. We may share data with
          trusted service providers who help us operate the service (e.g.,
          hosting, analytics, payments), strictly for the purposes described in
          this policy and subject to appropriate safeguards.
        </p>

        <h3>4. Data Retention</h3>
        <p>
          We retain personal data for as long as necessary to provide the
          service and fulfill the purposes described here, or as required by
          law. You may request deletion of your account and associated data,
          subject to legal or legitimate business requirements.
        </p>

        <h3>5. Your Rights</h3>
        <p>
          Depending on your location, you may have rights to access, correct,
          delete, or export your personal data, and to object to or restrict
          certain processing. We will honor applicable legal rights and requests
          where feasible.
        </p>

        <h3>6. Security</h3>
        <p>
          We implement reasonable technical and organizational measures to
          protect personal data. However, no method of transmission or storage
          is 100% secure, and we cannot guarantee absolute security.
        </p>

        <h3>7. Children’s Privacy</h3>
        <p>
          The service is not intended for children under the age where parental
          consent is required by applicable law. We do not knowingly collect
          personal data from such users.
        </p>

        <h3>8. International Transfers</h3>
        <p>
          Your data may be processed in countries outside of your own. When we
          transfer personal data, we use safeguards designed to protect your
          information consistent with applicable laws.
        </p>

        <h3>9. Changes to This Policy</h3>
        <p>
          We may update this Privacy Policy from time to time. Material changes
          will be communicated by updating this page and/or via in-app or email
          notice where appropriate. Continued use of the service after changes
          become effective constitutes acceptance of the updated policy.
        </p>
      </section>

      <section id="contact" class="section">
        <h2>Contact</h2>
        <p>
          For questions about these Terms or our Privacy practices, please
          reach out through the in-app support or the official support channel
          provided within your account.
        </p>
      </section>

      <div class="nav-actions">
        <router-link class="btn" to="/subscriptions">Back to subscriptions</router-link>
      </div>
    </main>
  </div>
</template>

<style scoped>
.legal {
  padding: 20px;
  margin-left: 75px; /* align with app sidebar on learn host */
  color: var(--text-color);
  background: var(--app-bg);
}

.header h1 {
  margin: 0 0 8px;
  font-weight: 800;
  letter-spacing: -0.01em;
}
.intro {
  margin: 0 0 12px;
  color: color-mix(in oklab, var(--text-color) 78%, transparent);
}

.toc {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid color-mix(in oklab, var(--text-color) 12%, transparent);
  border-radius: 10px;
  background: var(--sidebar-bg);
  font-size: 0.95rem;
}
.toc a {
  color: var(--text-color);
  text-decoration: none;
}
.toc a:hover {
  text-decoration: underline;
}

.content {
  margin-top: 14px;
  max-width: 900px;
}

.section {
  margin: 20px 0;
  padding: 14px;
  border-radius: 12px;
  background: var(--sidebar-bg);
  border: 1px solid color-mix(in oklab, var(--text-color) 10%, transparent);
}

.section h2 {
  margin: 0 0 8px;
  font-size: 1.4rem;
  letter-spacing: -0.01em;
}
.section h3 {
  margin: 14px 0 6px;
  font-size: 1.05rem;
}
.section p {
  margin: 0 0 10px;
  line-height: 1.6;
}
.section ul {
  margin: 0 0 10px 18px;
  padding: 0;
  line-height: 1.6;
}

.nav-actions {
  margin-top: 18px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  background: var(--hover-bg);
  color: var(--text-color);
  border: 1px solid color-mix(in oklab, var(--text-color) 12%, transparent);
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.06s ease, background 0.15s ease;
}
.btn:hover {
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 980px) {
  .legal {
    margin-left: 0;
  }
}
</style>
